import React, { useCallback, useEffect, useState } from 'react';

import isEqual from 'lodash-es/isEqual';
import { useIntl } from 'react-intl';
import { FlatList } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { StoreLocatorEmptyStates } from 'components/store-locator-empty-states';
import { useGeolocation } from 'state/geolocation';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { ClickEventComponentNames, CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useStoreContext } from 'state/store';

import { LoadingIndicator } from '../loading-indicator';
import { StoreCard } from '../store-card';

interface IStoreCardListProps {
  currentTabHasError?: boolean;
  isSearching?: boolean;
  restaurants: IRestaurant[];
  tab: OrderLocatorTab;
  onCardPress?: (storeId?: string, storeNumber?: string, storeAddress?: string) => void;
  selectStoreFormField?: (storeId: string) => void;
  isInSupportForm?: boolean;
}

const StoreCardList: React.FC<IStoreCardListProps> = ({
  currentTabHasError = false,
  isSearching,
  restaurants = [],
  tab,
  onCardPress,
  selectStoreFormField,
  isInSupportForm,
}) => {
  const { formatMessage } = useIntl();
  const { logButtonClick } = useMParticleContext();
  const { store, noStoreSelected } = useStoreContext();
  const { coordinatesAvailable, isPermissionGranted } = useGeolocation();
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

  useEffect(() => {
    if (noStoreSelected || !store._id) {
      return;
    }

    setSelectedStoreId(store._id);
  }, [store, noStoreSelected]);

  const handleOnCardPress = useCallback(
    ({ restaurant }: { restaurant: IRestaurant }) => {
      if (onCardPress && restaurant) {
        onCardPress(
          String(restaurant.id),
          String(restaurant.number),
          String(restaurant.physicalAddress?.address1)
        );
        return;
      }

      setSelectedStoreId(prevRestaurantId => {
        if (prevRestaurantId === restaurant._id || !restaurant._id) {
          return null;
        }
        logButtonClick({
          attributes: {
            StoreId: restaurant._id,
            Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
            name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
            component: ClickEventComponentNames.BUTTON,
          },
        });
        return restaurant._id;
      });
    },
    [onCardPress, logButtonClick]
  );

  const renderItem = useCallback(
    ({ item }: { item: IRestaurant }) => (
      <StoreCard
        restaurant={item}
        onCardPress={handleOnCardPress}
        isSelected={item._id === selectedStoreId}
        selectStoreFormField={selectStoreFormField}
        isInSupportForm={isInSupportForm}
      />
    ),
    [handleOnCardPress, selectedStoreId, selectStoreFormField, isInSupportForm]
  );

  if (isSearching) {
    return <LoadingIndicator ariaLabel={formatMessage({ id: 'locationSearch' })} />;
  }

  if (restaurants.length === 0) {
    return (
      <StoreLocatorEmptyStates
        tab={tab}
        locationPermissionsGranted={isPermissionGranted}
        coordinatesAvailable={coordinatesAvailable}
        currentTabError={currentTabHasError}
      />
    );
  }

  return (
    <FlatList
      keyExtractor={(item, index) => item?.id || index + '_restaurant'}
      data={restaurants}
      renderItem={renderItem}
    />
  );
};

// StoreCardList.whyDidYouRender = true;

export default React.memo(StoreCardList, (prevProps, nextProps) => {
  if (prevProps.tab !== nextProps.tab) {
    return false;
  }
  if (prevProps.isSearching !== nextProps.isSearching) {
    return false;
  }
  if (prevProps.currentTabHasError !== nextProps.currentTabHasError) {
    return false;
  }
  if (
    !isEqual(
      prevProps?.restaurants?.map(x => x._id),
      nextProps?.restaurants?.map(x => x._id)
    )
  ) {
    return false;
  }

  return true;
});
